// src/utils/DataContext.js
import { useState, useEffect, createContext, useRef, useContext } from "react";
import axios from "axios";
import { useTranslation } from "react-i18next";
import { UserAuth } from "./AuthContext";
import { sub } from "date-fns";

export const DataContext = createContext(null);

export default function DataProvider({ children }) {
  // * get userData
  const { userData } = useContext(UserAuth);
  const { token, userType, userId, supplierId, customerId, organizationId } =
    userData;

  const { t } = useTranslation("general");

  const [searchError, setSearchError] = useState(false);
  const [searchErrorMessage, setSearchErrorMessage] = useState("");

  const currencies = [
    { title: "USD", value: "USD" },
    { title: "IQD", value: "IQD" },
    { title: "EURO", value: "EURO" },
    { title: "RMB", value: "RMB" },
    { title: "TRY", value: "TRY" },
    { title: "AED", value: "AED" },
  ];

  // * Main APP data
  const [invoices, setInvoices] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [subCompanies, setSubCompanies] = useState([]);

  const [selectedInvoicesStatus, setSelectedInvoicesStatus] = useState({
    title: "Active",
    value: "active",
  });

  const [selectedShippedContainersStatus, setSelectedShippedContainersStatus] =
    useState({
      title: "Active",
      value: "active",
    });

  const [
    selectedInvoicesOfMultiContsStatus,
    setSelectedInvoicesOfMultiContsStatus,
  ] = useState({
    title: "Active",
    value: "active",
  });

  const [selectedTitle, setSelectedTitle] = useState({
    title: t("invoiceNumber"),
    value: "invoiceNumber",
  });
  const [searchQuery, setSearchQuery] = useState("");

  const [searchTerm, setSearchTerm] = useState("");

  const [selectedSupplier, setSelectedSupplier] = useState({});
  const [selectedCustomer, setSelectedCustomer] = useState({});
  const [selectedSubCompany, setSelectedSubCompany] = useState(
    JSON.parse(localStorage.getItem("subCompany")) || null,
  );
  const [selectedDate, setSelectedDate] = useState(null);

  // * Pagination for Invoices
  const [invoicesCurrentPage, setInvoicesCurrentPage] = useState(1);
  const [invoicesTotalPages, setInvoicesTotalPages] = useState(1);
  const [invoicesTotalRecords, setInviocesTotalRecords] = useState(1);

  const [networkError, setNetworkError] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const [subCompaniesCurrentPage, setSubCompaniesCurrentPage] = useState(1);

  const [shippedContainers, setShippedContainers] = useState([]);

  // * Pagination for the ShippedContainers
  const [shippedContainersCurrentPage, setShippedContainersCurrentPage] =
    useState(1);
  const [shippedContainersTotalPages, setShippedContainersTotalPages] =
    useState(1);
  const [shippedContainersCount, setShippedContainersCount] = useState(1);

  const [currentBalance, setCurrentBalance] = useState(null);

  const [supplierModal, setSupplierModal] = useState(false);
  const [supplierFields, setSupplierFields] = useState({
    supplierName: "",
    supplierCompany: "",
    supplierAddress: "",
    supplierCountry: "",
    supplierWebsite: "",
    supplierCurrency: "",
    supplierCity: "",
    supplierPhoneNumber: "",
    bankAccounts: [],

    supplierEmail: "",
    supplierGoodsType: "",
    supplierBusinessSector: "",
    supplierCountryCode: "",
    supplierRelationWithSupplier: "",
  });

  const [supplierCurrency, setSupplierCurrency] = useState({
    title: "USD",
    value: "USD",
  });

  const [shippedContainersBalances, setShippedContainersBalances] =
    useState(null);

  const [invoicesOfMultiContainers, setInvoicesOfMultiContainers] = useState(
    [],
  );
  const [balancesOfInvOfMultiConts, setBalancesOfInvOfMultiConts] =
    useState(null);

  const [totalInvOfMultiContsRecords, setTotalInvOfMultiContsRecords] =
    useState(0);
  const [totalPagesOfInvOfMultiConts, setTotalPagesOfInvOfMultiConts] =
    useState(1);
  const [currentPageOfInvOfMultiConts, setCurrentPageOfInvOfMultiConts] =
    useState(1);

  useEffect(() => {
    const getNewInvoicesPage = async () => {
      if (selectedInvoicesStatus.value === "active") {
        if (searchQuery) {
          await handleSearch();
        } else {
          await fetchInvoices();
        }
      } else if (selectedInvoicesStatus.value === "archived") {
        await fetchArchivedInvoices(selectedSupplier?.id);
      } else {
        console.log("Please select valid type of invoices");
      }
    };

    if (selectedSupplier?.id) {
      getNewInvoicesPage();
    }
  }, [invoicesCurrentPage]);

  const fetchShippedContainers = async (
    supplierId,
    customerId,
    subCompanyId,
    searchType,
    searchQuery,
    noCustomer = false,
  ) => {
    try {
      setLoading(true);

      const params = {
        page: shippedContainersCurrentPage,
        limit: 5,
        query: searchQuery,
        type: searchType,
      };

      if (subCompanyId !== "All") {
        params.subCompanyId = subCompanyId || selectedSubCompany?.id;
      }

      const route = noCustomer
        ? "/getShippedContainersWithoutC"
        : "/getShippedContainers";

      const response = await axios.get(`${process.env.REACT_APP_URL}${route}`, {
        params,
        headers: {
          Authorization: `Bearer ${token}`,
          "Supplier-Id": supplierId || selectedSupplier?.id,
          "customer-Id": customerId || selectedCustomer?.id,
          "User-Id": userId,
          "Organization-Id": organizationId,
        },
      });

      const data = response?.data;

      if (data) {
        setShippedContainers(data?.shippedContainers);
        setShippedContainersTotalPages(data?.totalPages);
        setShippedContainersCount(data?.totalShippedContainers);
        setSelectedSupplier(data?.currentSupplier);
        setSelectedCustomer(data?.currentCustomer);
        setShippedContainersBalances(data?.balance?._sum);

        // if (currentSupplier?.id === "All") {
        //   setSelectedSupplier({ id: "All", name: `${t("all")}` });
        // } else {
        //   setSelectedSupplier(data?.currentSupplier);
        // }
        // const currentCustomer = data?.currentCustomer;

        // if (currentCustomer?.id === "All") {
        //   setSelectedCustomer({ id: "All", name: `${t("all")}` });
        // } else {
        //   setSelectedCustomer(data?.currentCustomer);
        // }
      }

      if (response?.status === 200) {
        setLoading(false);
      }

      return response.data;
    } catch (error) {
      setLoading(false);
      console.error("Error fetching data:", error);
    }
  };

  const fetchInvoices = async (supplierId) => {
    try {
      const token = localStorage.getItem("token");
      const userId = localStorage.getItem("userId");
      const organizationId = localStorage.getItem("organizationId");

      setLoading(true);

      const response = await axios.get(
        `${process.env.REACT_APP_URL}/getInvoices`,
        {
          params: { page: invoicesCurrentPage, limit: 10 },
          headers: {
            Authorization: `Bearer ${token}`,
            "User-Id": userId,
            "organization-Id": organizationId,
            "Supplier-Id": supplierId || selectedSupplier?.id,
          },
        },
      );

      if (response.status === 200) {
        setInvoices(response.data?.invoices);
        setInvoicesTotalPages(response.data?.totalPages);
        setInviocesTotalRecords(response.data?.totalInvoices);
        setCurrentBalance(response.data?.currentBalance);
        setLoading(false);
      }

      const resCurrentSupplier = response?.data?.currentSupplier;

      if (resCurrentSupplier) {
        setSelectedSupplier(resCurrentSupplier);
      }
      return response.data;
    } catch (error) {
      console.error("Error fetching data:", error);
      setLoading(false);
    }
  };

  const fetchContainers = async (page) => {
    try {
      setLoading(true);
      const token = localStorage.getItem("token");

      const userId = parseInt(localStorage.getItem("userId"), 10);
      const organizationId = localStorage.getItem("organizationId");

      const response = await axios.get(
        `${process.env.REACT_APP_URL}/getContainersPrices`,
        {
          params: { page: page, limit: 10 },
          headers: {
            Authorization: `Bearer ${token}`,
            "User-Id": userId,
            "Organization-Id": organizationId,
            "Supplier-Id": selectedSupplier?.id,
          },
        },
      );

      if (response.status === 200) {
        const resCurrentSupplier = response?.data?.currentSupplier;

        if (resCurrentSupplier) {
          setSelectedSupplier({ ...resCurrentSupplier });
        }
        return response;
      }
    } catch (error) {
      setNetworkError(true);
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveSupplier = async () => {
    const token = localStorage.getItem("token");
    const userId = parseInt(localStorage.getItem("userId"), 10);
    const organizationId = localStorage.getItem("organizationId");

    const requiredFields = [supplierFields.supplierName];

    // * if there is a selected bank, make other bank account data required
    if (supplierFields?.bankAccounts[0]?.bank?.id) {
      requiredFields.push(supplierFields?.bankAccounts[0]?.branch?.id);
      requiredFields.push(supplierFields?.bankAccounts[0]?.accountNumber);
      requiredFields.push(supplierFields?.bankAccounts[0]?.accountCurrency);
    }

    if (requiredFields?.some((field) => !field)) {
      setError("emptyInputFields");
      return;
    }

    try {
      const newSupplier = await axios.post(
        `${process.env.REACT_APP_URL}/createSupplier`,
        {
          userId: userId,
          organizationId: organizationId || null,
          ...supplierFields,
          supplierCurrency: supplierCurrency.value,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      if (!newSupplier) {
        console.error("Failed to save the supplier", error);
      }
      // setSupplierModal(false);
      setSupplierFields({
        supplierName: "",
        supplierCompany: "",
        supplierAddress: "",
        supplierCountry: "",
        supplierWebsite: "",
        supplierCurrency: "",
        supplierCity: "",
        supplierPhoneNumber: "",
        supplierEmail: "",
        supplierGoodsType: "",
        supplierBusinessSector: "",
        supplierCountryCode: "",
        supplierRelationWithSupplier: "",
        bankAccounts: [],
      });
      setSupplierCurrency({
        title: "USD",
        value: "USD",
      });

      const newSupplierData = newSupplier?.data?.newSupplier;
      setSuppliers((prev) => [newSupplierData, ...prev]);

      if (newSupplier?.data?.currentSupplierId) {
        setSelectedSupplier(newSupplierData);
      }
      return newSupplier;
    } catch (error) {
      setError(error?.response?.data?.error);

      setNetworkError(true);
      console.error("Failed to save the supplier", error);
    }
  };

  const handleSelectSupplier = async (supplier, page = "invoices") => {
    switch (page) {
      case "invoices":
        await fetchInvoices(supplier?.id);
        break;

      case "shippedContainers":
        if (selectedShippedContainersStatus.value === "active") {
          await fetchShippedContainers(
            supplier?.id,
            selectedCustomer?.id,
            selectedSubCompany?.id,
          );
        } else {
          await fetchArchivedShippedContainers(
            supplier?.id,
            selectedCustomer?.id,
            selectedSubCompany?.id,
          );
        }
        break;

      case "invoicesOfMultiContainers":
        if (selectedInvoicesOfMultiContsStatus.value === "active") {
          await fetchInvoicesOfMultiContainers(
            supplier?.id,
            selectedCustomer?.id,
            selectedSubCompany?.id,
          );
        } else {
          await fetchArchivedInvoicesOfMultiContainers(
            supplier?.id,
            selectedCustomer?.id,
            selectedSubCompany?.id,
          );
        }
        break;

      case "noCustomerContainers":
        if (selectedShippedContainersStatus.value === "active") {
          await fetchShippedContainers(
            supplier?.id,
            null,
            selectedSubCompany?.id,
            null,
            null,
            true,
          );
        } else {
          await fetchArchivedShippedContainers(
            supplier?.id,
            null,
            selectedSubCompany?.id,
            null,
            null,
            true,
          );
        }
        break;

      case "noCustomerInvoices":
        await fetchInvoicesOfMultiContainers(
          supplier?.id,
          null,
          selectedSubCompany?.id,
          null,
          null,
          null,
          null,
          true,
        );
        break;

      case "noRequest":
        setSearchQuery("");
        break;

      default:
        await fetchContainers();
        break;
    }

    setSearchQuery("");
    setSelectedSupplier(supplier);
  };

  const handleSearch = async (query) => {
    const token = localStorage.getItem("token");
    const userId = parseInt(localStorage.getItem("userId"), 10);

    if (typeof query !== "string") {
      query = searchQuery;
    }

    if (selectedSupplier?.id) {
      try {
        setLoading(true);
        const response = await axios.get(
          `${process.env.REACT_APP_URL}/searchInvoices`,
          {
            params: {
              query: query,
              title: selectedTitle.value,
              dateToSearchFor:
                selectedTitle.value === "date" ? selectedDate : "",
              page: invoicesCurrentPage,
              invoicesStatus: selectedInvoicesStatus.value,
              limit: 10,
            },
            headers: {
              Authorization: `Bearer ${token}`,
              "User-Id": userId,
              "Supplier-Id": selectedSupplier?.id,
            },
          },
        );

        if (!response) {
          console.error("Failed to get the search results");
        }

        if (response?.data?.searchResults.length) {
          setSearchError(false);
        }

        setInvoices(response?.data?.searchResults || []);
        setInvoicesCurrentPage(response?.data?.currentPage);
        setInviocesTotalRecords(response?.data?.totalInvoices);
        setInvoicesTotalPages(response?.data?.totalPages);
        setCurrentBalance(response?.data?.currentBalance);
        setLoading(false);
      } catch (error) {
        setInvoices([]);
        setSearchError(true);
        setSearchErrorMessage(error.response.data.error);
        console.error("Error searching invoices:", error);
        setLoading(false);
      }
    }
  };

  const getSuppliers = async (page, limit, isDropDown = false, searchTerm) => {
    const token = localStorage.getItem("token");
    // Ensure userId is stored as a number
    const userId = parseInt(localStorage.getItem("userId"), 10);
    const organizationId =
      parseInt(localStorage.getItem("organizationId"), 10) || null;

    try {
      if (!isDropDown) {
        setLoading(true);
      }

      const suppliers = await axios.get(
        `${process.env.REACT_APP_URL}/getSuppliers`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "User-Id": userId,
            "organization-Id": organizationId,
          },

          // added parameters to the get supplier
          params: {
            page: page ?? 1,
            limit: limit ?? 5,
            search: searchTerm,
          },
        },
      );

      if (!suppliers) {
        console.error("Failed to get the suppliers", error);
      }

      // changed the suppliers.data.data -> suppliers.data.suppliers
      const existingSupliers = suppliers?.data?.suppliers;
      // const existingSupliers = suppliers?.data?.d;

      setSuppliers(existingSupliers);

      if (suppliers?.status === 200) {
        setLoading(false);
      }

      return suppliers;
    } catch (error) {
      setNetworkError(true);
      console.error("Failed to get the suppliers", error);
      setLoading(false);
    }
  };

  // This function only returns id and name if the supplier to save bandwidth
  const getFilteredSuppliers = async (
    page,
    limit,
    isDropDown = false,
    searchTerm,
  ) => {
    const token = localStorage.getItem("token");
    // Ensure userId is stored as a number
    const userId = parseInt(localStorage.getItem("userId"), 10);
    const organizationId =
      parseInt(localStorage.getItem("organizationId"), 10) || null;

    try {
      if (!isDropDown) {
        setLoading(true);
      }

      const suppliers = await axios.get(
        `${process.env.REACT_APP_URL}/supplier/filteredSuppliers`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "User-Id": userId,
            organizationId: organizationId,
          },

          // added parameters to the get supplier
          params: {
            page: page ?? 1,
            limit: limit ?? 5,
            search: searchTerm,
          },
        },
      );

      if (!suppliers) {
        console.error("Failed to get the suppliers", error);
      }

      // changed the suppliers.data.data -> suppliers.data.suppliers
      const existingSupliers = suppliers?.data?.suppliers;
      // const existingSupliers = suppliers?.data?.d;

      setSuppliers(existingSupliers);

      if (suppliers?.status === 200) {
        setLoading(false);
      }

      return suppliers;
    } catch (error) {
      setNetworkError(true);
      console.error("Failed to get the suppliers", error);
      setLoading(false);
    }
  };

  const getCustomers = async (page, limit, isDropDown = false, searchTerm) => {
    try {
      if (!isDropDown) {
        setLoading(true);
      }
      const customers = await axios.get(
        `${process.env.REACT_APP_URL}/getCustomers`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "User-Id": userId,
            "organization-Id": organizationId,
          },
          params: {
            page: page ?? 1,
            limit: limit ?? 5,
            search: searchTerm,
          },
        },
      );

      if (!customers) {
        console.error("Failed to get the cusotmers", error);
      }

      const existingCustomers = customers?.data?.customers;

      setCustomers(existingCustomers);

      existingCustomers?.map((customer) => {
        if (`${customer.id}` === customerId) {
          setSelectedCustomer(customer);
        }
      });

      if (customerId === "All") {
        setSelectedCustomer({ id: "All", name: "All" });
      }

      if (customers?.status === 200) {
        setLoading(false);
      }

      return customers;
    } catch (error) {
      setNetworkError(true);
      console.error("Failed to get the Customers", error);
      setLoading(false);
    }
  };

  const getSubCompanies = async (
    page,
    limit,
    isDropDown = false,
    searchTerm,
  ) => {
    try {
      if (isNaN(userData?.organizationId)) {
        return;
      }

      if (!isDropDown) {
        setLoading(true);
      }

      const response = await axios.get(
        `${process.env.REACT_APP_URL}/api/subCompany`,
        {
          headers: {
            Authorization: `Bearer ${userData?.token}`,
            "organization-Id": parseInt(userData?.organizationId),
          },
          params: {
            page: page ?? subCompaniesCurrentPage,
            limit: limit ?? 10,
            search: searchTerm,
          },
        },
      );

      if (response?.status === 200) {
        setLoading(false);
      }

      if (response?.data?.subCompanies) {
        setSubCompanies(response?.data?.subCompanies);
        return response;
        // setFilteredSubCompanies(response?.data?.subCompanies);
        // setTotalPages(response.data.totalPages); // Set total pages from the response
        // setTotalRecords(response.data.totalSubCompanies); // Set total records from the response
      } else {
        console.error("Failed to get the SubCompanies");
      }
    } catch (error) {
      setNetworkError(true);
      console.error("Failed to get the SubCompanies", error);
      setLoading(false);
    }
  };

  // This endpoint only returns the id and name of the subCompanies to save bandwidth
  const getFilteredSubCompanies = async (
    page,
    limit,
    isDropDown = false,
    searchTerm,
  ) => {
    try {
      if (isNaN(userData?.organizationId)) {
        return;
      }

      if (!isDropDown) {
        setLoading(true);
      }

      const response = await axios.get(
        `${process.env.REACT_APP_URL}/api/subCompany/filtered`,
        {
          headers: {
            Authorization: `Bearer ${userData?.token}`,
            organizationId: parseInt(userData?.organizationId),
          },
          params: {
            page: page ?? subCompaniesCurrentPage,
            limit: limit ?? 5,
            search: searchTerm,
          },
        },
      );

      if (response?.status === 200) {
        setLoading(false);
      }

      if (response?.data?.subCompanies) {
        setSubCompanies(response?.data?.subCompanies);
        return response;
        // setFilteredSubCompanies(response?.data?.subCompanies);
        // setTotalPages(response.data.totalPages); // Set total pages from the response
        // setTotalRecords(response.data.totalSubCompanies); // Set total records from the response
      } else {
        console.error("Failed to get the SubCompanies");
      }
    } catch (error) {
      setNetworkError(true);
      console.error("Failed to get the SubCompanies", error);
      setLoading(false);
    }
  };

  const fetchArchivedInvoices = async (supplierId) => {
    const userId = parseInt(localStorage.getItem("userId"), 10);
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_URL}/getArchivedInvoices`,
        {
          params: { page: invoicesCurrentPage, limit: 10 },

          headers: {
            Authorization: `Bearer ${token}`,
            "User-Id": userId,
            "Supplier-Id": supplierId,
          },
        },
      );
      if (res.data) {
        setInvoices(res?.data?.invoices);
        setInvoicesTotalPages(res.data?.totalPages);
        setInviocesTotalRecords(res.data?.totalInvoices);
      }
    } catch (error) {
      setNetworkError(true);
      console.error("Falied to get the archived invoices", error);
    }
  };

  const fetchCurrentBalance = async () => {
    if (selectedSupplier?.id && selectedSupplier?.id !== "All") {
      try {
        setLoading(true);
        const token = localStorage.getItem("token");
        const userId = parseInt(localStorage.getItem("userId"));
        const organizationId = localStorage.getItem("organizationId");

        const response = await axios.get(
          `${process.env.REACT_APP_URL}/getCurrentBalance`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "User-Id": userId,
              "supplier-Id": selectedSupplier?.id,
              "organization-Id": organizationId,
            },
          },
        );

        setCurrentBalance(response?.data);

        if (response?.status === 200) {
          setLoading(false);
        }
      } catch (error) {
        setNetworkError(true);
        setLoading(false);
        console.error("Error fetching data:", error);
      }
    }
  };

  const handleShowSupplierModal = () => {
    setSupplierModal(true);
  };

  const handleSelectCustomer = async (customer, page) => {
    if (page === "invoicesOfMultiContainers") {
      if (selectedInvoicesOfMultiContsStatus.value === "active") {
        await fetchInvoicesOfMultiContainers(
          selectedSupplier?.id,
          customer?.id,
          selectedSubCompany?.id,
        );
      } else {
        fetchArchivedInvoicesOfMultiContainers(
          selectedSupplier?.id,
          customer?.id,
          selectedSubCompany?.id,
        );
      }
    } else if (page === "noRequest") {
      setSearchQuery("");
    } else {
      if (selectedShippedContainersStatus.value === "active") {
        await fetchShippedContainers(
          selectedSupplier?.id,
          customer?.id,
          selectedSubCompany?.id,
        );
      } else {
        await fetchArchivedShippedContainers(
          selectedSupplier?.id,
          customer?.id,
          selectedSubCompany?.id,
        );
      }
    }
    setSelectedCustomer(customer);

    // localStorage.setItem("customerId", customer?.id);
  };

  const handleSelectSubCompany = async (subCompany, page) => {
    if (page === "invoicesOfMultiContainers") {
      if (selectedInvoicesOfMultiContsStatus.value === "active") {
        await fetchInvoicesOfMultiContainers(
          selectedSupplier?.id,
          selectedCustomer?.id,
          subCompany?.id,
        );
      } else {
        fetchArchivedInvoicesOfMultiContainers(
          selectedSupplier?.id,
          selectedCustomer?.id,
          subCompany?.id,
        );
      }
    } else if (page === "noCustomerInvoices") {
      if (selectedInvoicesOfMultiContsStatus.value === "active") {
        await fetchInvoicesOfMultiContainers(
          selectedSupplier?.id,
          selectedCustomer?.id,
          subCompany?.id,
          null,
          null,
          null,
          null,
          true,
        );
      } else {
        fetchArchivedInvoicesOfMultiContainers(
          selectedSupplier?.id,
          selectedCustomer?.id,
          subCompany?.id,
          null,
          null,
          null,
          null,
          true,
        );
      }
    } else if (page === "noRequest") {
      setSearchQuery("");
    } else if (page === "noCustomerContainers") {
      if (selectedShippedContainersStatus.value === "active") {
        await fetchShippedContainers(
          selectedSupplier?.id,
          selectedCustomer?.id,
          subCompany?.id,
          null,
          null,
          true,
        );
      } else {
        await fetchArchivedShippedContainers(
          selectedSupplier?.id,
          selectedCustomer?.id,
          subCompany?.id,
          null,
          null,
          true,
        );
      }
    } else {
      if (selectedShippedContainersStatus.value === "active") {
        await fetchShippedContainers(
          selectedSupplier?.id,
          selectedCustomer?.id,
          subCompany?.id,
        );
      } else {
        await fetchArchivedShippedContainers(
          selectedSupplier?.id,
          selectedCustomer?.id,
          subCompany?.id,
        );
      }
    }
    setSelectedSubCompany(subCompany);
    localStorage.setItem(
      "subCompany",
      JSON.stringify({
        id: subCompany?.id,
        name: subCompany?.name,
        nameInAr: subCompany?.nameInAr,
      }),
    );
  };

  const fetchArchivedShippedContainers = async (
    supplierId,
    customerId,
    subCompanyId,
    searchType,
    searchQuery,
    noCustomer = false,
  ) => {
    const { token, userId, organizationId } = userData;

    const params = {
      page: shippedContainersCurrentPage,
      limit: 5,
    };

    if (searchType) {
      params.type = searchType;
      params.query = searchQuery;
    }

    if (subCompanyId !== "All") {
      params.subCompanyId = subCompanyId || selectedSubCompany?.id;
    }

    try {
      const route = noCustomer
        ? "/getArchivedShippedContainersWithoutC"
        : "/getArchivedShippedContainers";

      const res = await axios.get(`${process.env.REACT_APP_URL}${route}`, {
        params,

        headers: {
          Authorization: `Bearer ${token}`,
          "User-Id": userId,
          "Supplier-Id": supplierId,
          "Customer-Id": customerId,
          "Organization-Id": organizationId,
        },
      });
      if (res.data) {
        setShippedContainers(res?.data?.archivedShippedContainers);
        setShippedContainersTotalPages(res?.data?.totalPages);
        setShippedContainersCount(res?.data?.totalShippedContainers);

        // setInvoicesTotalPages(res.data?.totalPages);
        // setInviocesTotalRecords(res.data?.totalShippedContainers);
      }
    } catch (error) {
      setNetworkError(true);
      console.error("Falied to get the archived invoices", error);
    }
  };

  const fetchInvoicesOfMultiContainers = async (
    supplierId,
    customerId,
    subCompanyId,
    searchType,
    searchQuery,
    startDate,
    endDate,
    noCustomer = false,
  ) => {
    try {
      setLoading(true);

      const { userId, token, organizationId } = userData;
      const searchParams = { startDate, endDate };

      searchParams.page = currentPageOfInvOfMultiConts;
      searchParams.limit = 5;

      if (subCompanyId !== "All") {
        searchParams.subCompanyId = subCompanyId || selectedSubCompany?.id;
      }

      if (searchType) {
        searchParams.type = searchType;
        searchParams.query = searchQuery;
      }
      //  else {
      //   searchParams.type = "subCompany";
      //   searchParams.query = selectedSubCompany?.id;
      // }

      // const route = noCustomer
      //   ? "/getMultiContainersInvoicesWithoutC"
      //   : "/getMultiContainersInvoices";
      const route = noCustomer
        ? "/multiContainerInvoiceWithoutC/filteredMultiContainerInvoices"
        : "/getMultiContainersInvoices";

      // const response = await axios.get(`${process.env.REACT_APP_URL}${route}`, {
      //   params: {
      //     ...searchParams,
      //     supplierId: selectedSupplier?.id
      //   },
      //   headers: {
      //     Authorization: `Bearer ${token}`,
      //     "User-Id": userId,
      //     "Organization-Id": organizationId,
      //     "OrganizationId": organizationId,
      //     "Supplier-Id": supplierId || selectedSupplier?.id,
      //     "Customer-Id": customerId || selectedCustomer?.id,
      //   },
      // });
      const response = {};

      if (response.status === 200) {
        const data = response?.data;
        setInvoicesOfMultiContainers(data?.invoices);
        setBalancesOfInvOfMultiConts(data?.balance?._sum);
        setTotalInvOfMultiContsRecords(data?.totalInvoices);
        setTotalPagesOfInvOfMultiConts(data?.totalPages);

        return data;
      }
    } catch (error) {
      // setLoading(false);
      setNetworkError(true);
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchArchivedInvoicesOfMultiContainers = async (
    supplierId,
    customerId,
    subCompanyId,
    searchType,
    searchQuery,
    startDate,
    endDate,
    noCustomer = false,
  ) => {
    try {
      const searchParams = { startDate, endDate };

      searchParams.page = currentPageOfInvOfMultiConts;
      searchParams.limit = 5;

      if (searchType) {
        searchParams.type = searchType;
        searchParams.query = searchQuery;
      }

      if (subCompanyId !== "All") {
        searchParams.subCompanyId = subCompanyId || selectedSubCompany?.id;
      }

      setLoading(true);
      const token = localStorage.getItem("token");
      // Ensure userId is stored as a number
      const { userId, organizationId } = userData;

      const route = noCustomer
        ? "/getArchivedMultiContainersInvoicesWithoutC"
        : "/getArchivedMultiContainersInvoices";

      const response = await axios.get(`${process.env.REACT_APP_URL}${route}`, {
        params: searchParams,
        headers: {
          Authorization: `Bearer ${token}`,
          "Organization-Id": organizationId,
          "User-Id": userId,
          "Supplier-Id": supplierId || selectedSupplier?.id,
          "Customer-Id": customerId || selectedCustomer?.id,
        },
      });

      if (response.status === 200) {
        const data = response?.data;
        setInvoicesOfMultiContainers(data?.invoices);
        setTotalInvOfMultiContsRecords(data?.totalInvoices);
        setTotalPagesOfInvOfMultiConts(data?.totalPages);

        return data;
      }
    } catch (error) {
      // setLoading(false);
      setNetworkError(true);
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <DataContext.Provider
      value={{
        suppliers,
        setSuppliers,
        getSuppliers,
        getFilteredSuppliers,
        customers,
        setCustomers,
        getCustomers,
        supplierModal,
        setSupplierModal,
        selectedSupplier,
        setSelectedSupplier,
        selectedCustomer,
        selectedSubCompany,
        setSelectedSubCompany,
        searchTerm,
        setSearchTerm,
        setSelectedCustomer,
        handleSelectSupplier,
        handleSelectCustomer,
        handleShowSupplierModal,
        error,
        setError,
        networkError,
        setNetworkError,
        loading,
        setLoading,
        data: invoices,
        setData: setInvoices,
        shippedContainers,
        setShippedContainers,
        subCompanies,
        setSubCompanies,
        getSubCompanies,
        getFilteredSubCompanies,
        currentBalance,
        setCurrentBalance,
        handleSaveSupplier,
        supplierFields,
        supplierCurrency,
        setSupplierCurrency,
        setSupplierFields,
        invoicesCurrentPage,
        setInvoicesCurrentPage,
        invoicesTotalPages,
        invoicesTotalRecords,
        fetchInvoices,
        fetchContainers,
        fetchArchivedInvoices,
        fetchShippedContainers,
        handleSearch,
        selectedDate,
        setSelectedDate,
        setInvoicesTotalPages,
        setInviocesTotalRecords,
        selectedInvoicesStatus,
        setSelectedInvoicesStatus,
        selectedShippedContainersStatus,
        setSelectedShippedContainersStatus,
        selectedInvoicesOfMultiContsStatus,
        setSelectedInvoicesOfMultiContsStatus,
        searchQuery,
        setSearchQuery,
        selectedTitle,
        setSelectedTitle,
        currencies,
        searchError,
        searchErrorMessage,
        fetchCurrentBalance,
        shippedContainersCount,
        setShippedContainersCount,
        shippedContainersCurrentPage,
        setShippedContainersCurrentPage,
        shippedContainersTotalPages,
        handleSelectSubCompany,
        fetchArchivedShippedContainers,
        shippedContainersBalances,
        setShippedContainersBalances,
        // fetchShippedContainersBalance,
        fetchInvoicesOfMultiContainers,
        fetchArchivedInvoicesOfMultiContainers,
        invoicesOfMultiContainers,
        balancesOfInvOfMultiConts,
        setBalancesOfInvOfMultiConts,
        totalInvOfMultiContsRecords,
        setTotalInvOfMultiContsRecords,
        totalPagesOfInvOfMultiConts,
        currentPageOfInvOfMultiConts,
        setCurrentPageOfInvOfMultiConts,
        shippedContainersBalances,
        setShippedContainersBalances,
      }}
    >
      {children}
    </DataContext.Provider>
  );
}
